<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3';
import {
    BookOpen,
    CheckCircle2,
    ChevronRight,
    Database,
    FileText,
    FolderOpen,
    GitBranch,
    LayoutGrid,
    Lightbulb,
    Link2,
    Paperclip,
    Search,
    Settings,
    SlidersHorizontal,
    Upload,
    Zap,
} from 'lucide-vue-next';
import { onMounted, onUnmounted, ref } from 'vue';
import AppHeaderLayoutLanding from '@/layouts/app/AppHeaderLayoutLanding.vue';
import { login, register } from '@/routes';

withDefaults(
    defineProps<{
        canRegister: boolean;
    }>(),
    {
        canRegister: true,
    },
);

interface Step {
    number: number;
    id: string;
    icon: object;
    title: string;
    subtitle: string;
    description: string;
    tips: string[];
    screenshot: string;
    screenshotAlt: string;
}

const steps: Step[] = [
    {
        number: 1,
        id: 'projets',
        icon: FolderOpen,
        title: 'Créer un projet',
        subtitle: 'Point de départ de chaque revue',
        description:
            "Chaque revue de littérature commence par un projet. Cliquez sur « Nouveau projet », donnez-lui un nom explicite et une description. Rewio organise toutes vos données — articles, PDF, grilles — dans ce projet de façon isolée. Vous pouvez avoir plusieurs projets en parallèle et passer de l'un à l'autre à tout moment.",
        tips: [
            'Nommez votre projet avec la question PICO pour le retrouver facilement.',
            'Un projet = une revue systématique. Ne mélangez pas plusieurs sujets dans le même projet.',
        ],
        screenshot: '/tutorial/step-01-projects.png',
        screenshotAlt: 'Page liste des projets avec un projet de démo',
    },
    {
        number: 2,
        id: 'tableau-de-bord',
        icon: LayoutGrid,
        title: "Vue d'ensemble",
        subtitle: "Suivre l'avancement de votre revue en un coup d'œil",
        description:
            "Dès l'ouverture d'un projet, la Vue d'ensemble vous donne un aperçu complet : nombre d'articles importés, avancement du screening, statut des PDF associés et progression de l'extraction. C'est votre boussole tout au long de la revue.",
        tips: [
            'Les compteurs en rouge ou orange signalent des tâches en attente.',
            'Cliquez sur chaque carte pour accéder directement à la section concernée.',
        ],
        screenshot: '/tutorial/step-02-project-show.png',
        screenshotAlt: 'Tableau de bord du projet avec métriques',
    },
    {
        number: 3,
        id: 'configuration',
        icon: Settings,
        title: 'Configurer les critères',
        subtitle: "Définir les règles d'inclusion et d'exclusion",
        description:
            "Dans la section Configuration du projet, renseignez vos critères d'inclusion et d'exclusion, votre question PICO (Population, Intervention, Comparaison, Outcome) et les bases de données interrogées. Ces informations sont utilisées par l'IA pour proposer des décisions de sélection cohérentes avec votre protocole.",
        tips: [
            "Plus vos critères sont précis, plus les suggestions de l'IA seront pertinentes.",
            'Vous pouvez modifier les critères à tout moment, y compris en cours de screening.',
        ],
        screenshot: '/tutorial/step-03-settings.png',
        screenshotAlt:
            "Page de configuration des critères d'inclusion/exclusion",
    },
    {
        number: 4,
        id: 'equations',
        icon: Search,
        title: 'Équations de recherche',
        subtitle: 'Tracer la stratégie de recherche',
        description:
            'Documentez chaque équation de recherche utilisée dans vos bases de données (PubMed, Scopus, Web of Science…). Rewio conserve la trace de vos requêtes avec la date, la base interrogée et le nombre de résultats. Cette traçabilité est indispensable pour le reporting PRISMA et la reproductibilité de votre revue.',
        tips: [
            'Ajoutez une équation par base de données que vous avez interrogée.',
            'Le nombre de résultats saisi ici alimente automatiquement le diagramme PRISMA.',
        ],
        screenshot: '/tutorial/step-04-equations.png',
        screenshotAlt:
            'Liste des équations de recherche avec résultats par base de données',
    },
    {
        number: 5,
        id: 'zotero',
        icon: Link2,
        title: 'Connecter Zotero',
        subtitle: "Qu'est-ce que Zotero et pourquoi l'utiliser ?",
        description:
            "Zotero est un logiciel gratuit de gestion bibliographique utilisé par des millions de chercheurs. Il permet de sauvegarder des articles depuis PubMed, Scopus, Google Scholar ou le web en un clic, et de les organiser dans des bibliothèques. En connectant votre compte Zotero à Rewio via OAuth, vos références arrivent automatiquement dans votre projet — sans aucun export/import manuel de fichier. Titre, auteurs, DOI, résumé et même les PDF attachés dans Zotero sont tous importés. La connexion s'effectue depuis la section Import de votre projet en cliquant sur « Connecter Zotero » : vous êtes redirigé vers Zotero pour autoriser l'accès, puis la connexion est active pour tous vos projets.",
        tips: [
            "Vous n'avez pas Zotero ? Vous pouvez aussi importer un fichier RIS, BibTeX ou CSV exporté depuis PubMed, Scopus, Web of Science, etc.",
            'Si vous avez plusieurs collections Zotero, vous choisissez lesquelles importer dans chaque projet.',
            'Chaque article importé est dédupliqué automatiquement — pas de doublons entre vos fichiers.',
        ],
        screenshot: '/tutorial/step-zotero.png',
        screenshotAlt:
            "Page d'intégration Zotero avec bouton de connexion et liste des champs importés",
    },
    {
        number: 6,
        id: 'import',
        icon: Upload,
        title: 'Importer les références',
        subtitle: 'Depuis Zotero ou un fichier RIS / BibTeX / CSV',
        description:
            'Une fois Zotero connecté, ouvrez la section Import de votre projet et importez les références de la collection souhaitée en un clic. Vous pouvez aussi déposer directement un fichier RIS, BibTeX ou CSV. Toutes vos références arrivent dans la bibliothèque du projet, prêtes à être sélectionnées. Les doublons entre plusieurs fichiers sont détectés et supprimés automatiquement.',
        tips: [
            'Importez vos fichiers RIS un par base de données pour garder la traçabilité.',
            'Le nombre total de références importées apparaît sur le tableau de bord et alimente le PRISMA.',
        ],
        screenshot: '/tutorial/step-05-import.png',
        screenshotAlt:
            "Page d'import avec connexion Zotero et upload de fichiers",
    },
    {
        number: 7,
        id: 'screening',
        icon: SlidersHorizontal,
        title: 'Trier les articles',
        subtitle: 'Inclure ou exclure chaque référence',
        description:
            "Parcourez la liste de vos articles : titre, résumé et mots-clés s'affichent en un coup d'œil. Cliquez ✓ pour inclure ou ✗ pour exclure, et notez votre justification. L'IA peut analyser chaque article selon vos critères et vous proposer une décision avec un score de confiance — vous gardez toujours le dernier mot.",
        tips: [
            "Activez les suggestions IA pour pré-trier rapidement des centaines d'articles.",
            'Utilisez le filtre « Non décidé » pour ne traiter que les articles en attente.',
            "Notez toujours la raison d'exclusion pour justifier votre PRISMA.",
        ],
        screenshot: '/tutorial/step-06-screening.png',
        screenshotAlt:
            "Page de screening avec liste d'articles et panneau de décision",
    },
    {
        number: 8,
        id: 'screening-detail',
        icon: FileText,
        title: 'Décision article par article',
        subtitle: 'Panneau de détail et justification',
        description:
            "Cliquez sur un article pour ouvrir le panneau de détail. Vous y voyez le titre complet, les auteurs, le résumé, et la suggestion IA si disponible. Inscrivez votre justification de rejet ou d'inclusion dans le champ prévu, puis passez à l'article suivant avec la flèche de navigation ou le raccourci clavier.",
        tips: [
            'Utilisez les flèches ← → pour naviguer rapidement sans repasser par la liste.',
            'La justification est exportée dans le rapport final : soyez précis.',
        ],
        screenshot: '/tutorial/step-07-screening-detail.png',
        screenshotAlt:
            "Panneau de détail d'un article avec suggestion IA et champ de décision",
    },
    {
        number: 9,
        id: 'associations',
        icon: Paperclip,
        title: 'Associer les PDF',
        subtitle: 'Relier chaque article à son texte intégral',
        description:
            "Pour les articles inclus, reliez chaque PDF à son article. L'import se fait par glisser-déposer ou en sélectionnant vos fichiers. Rewio tente automatiquement de faire correspondre les PDF via le DOI, le titre ou le numéro PMID. Vous pouvez aussi faire la liaison manuellement en quelques clics.",
        tips: [
            'Uploadez tous vos PDF en une fois, le matching automatique gère le reste.',
            'Si un PDF ne se lie pas automatiquement, utilisez la recherche manuelle par titre.',
        ],
        screenshot: '/tutorial/step-08-associations.png',
        screenshotAlt:
            "Page d'association PDF avec articles et statut de liaison",
    },
    {
        number: 10,
        id: 'extraction',
        icon: Database,
        title: 'Extraire les données',
        subtitle: "Grille personnalisable, pré-remplie par l'IA",
        description:
            "Définissez vos colonnes d'extraction (population, intervention, résultats, biais…). Pour chaque article inclus, l'IA lit le PDF et pré-remplit la grille. Vous vérifiez, corrigez et complétez chaque cellule. La grille est exportable en CSV ou Excel pour vos analyses statistiques.",
        tips: [
            "Créez vos colonnes selon votre grille PICO avant de lancer l'extraction IA.",
            "Les cellules pré-remplies par l'IA sont indiquées par une icône — vérifiez-les toujours.",
        ],
        screenshot: '/tutorial/step-09-extraction.png',
        screenshotAlt:
            'Grille extraction avec colonnes personnalisées et cellules IA',
    },
    {
        number: 11,
        id: 'prisma',
        icon: GitBranch,
        title: 'Diagramme PRISMA',
        subtitle: 'Généré automatiquement depuis vos décisions',
        description:
            "Le diagramme PRISMA 2020 est construit automatiquement à partir de vos équations, de vos décisions de screening et de vos articles inclus. Chaque case se remplit au fil de votre travail : identification, sélection, éligibilité, inclusion. Exportez-le en PNG pour l'insérer dans votre article.",
        tips: [
            'Vérifiez que vos équations de recherche ont bien un nombre de résultats renseigné.',
            'Le diagramme se met à jour en temps réel à chaque décision de screening.',
        ],
        screenshot: '/tutorial/step-10-prisma.png',
        screenshotAlt: 'Diagramme PRISMA 2020 généré automatiquement',
    },
    {
        number: 12,
        id: 'editeur',
        icon: BookOpen,
        title: 'Rédiger la revue',
        subtitle: 'Éditeur intégré pour la rédaction finale',
        description:
            "L'éditeur de texte intégré vous permet de rédiger votre revue directement dans Rewio. Insérez le diagramme PRISMA, la grille d'extraction et les références en quelques clics. Un assistant IA peut vous aider à structurer et reformuler vos sections.",
        tips: [
            "Utilisez la barre d'outils pour insérer un tableau d'extraction, une figure ou le diagramme PRISMA.",
            'Votre rédaction est sauvegardée automatiquement à chaque frappe.',
        ],
        screenshot: '/tutorial/step-11-editor.png',
        screenshotAlt:
            "Éditeur de texte avec barre d'outils et zone de rédaction",
    },
    {
        number: 13,
        id: 'bibliographie',
        icon: Zap,
        title: 'Exporter',
        subtitle: 'Références, données et diagrammes',
        description:
            "Depuis la section Bibliographie, exportez vos références incluses au format APA, Vancouver ou BibTeX en un clic. Depuis la grille d'extraction, exportez en CSV ou Excel. Le PRISMA s'exporte en PNG haute résolution. Toutes vos données restent les vôtres et vous pouvez les récupérer à tout moment.",
        tips: [
            'Exportez la bibliographie en APA pour coller directement dans Word.',
            'Le CSV de la grille est compatible avec SPSS, R et Excel.',
        ],
        screenshot: '/tutorial/step-12-bibliography.png',
        screenshotAlt:
            'Section Bibliographie avec liste de références APA et boutons export',
    },
];

const activeStep = ref(1);
const sidebarRef = ref<HTMLElement | null>(null);

function scrollToStep(n: number) {
    activeStep.value = n;
    const el = document.getElementById('step-' + n);

    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}

function onScroll() {
    const scrollY = window.scrollY + 180;

    for (let i = steps.length - 1; i >= 0; i--) {
        const el = document.getElementById('step-' + steps[i].number);

        if (el && el.offsetTop <= scrollY) {
            activeStep.value = steps[i].number;
            break;
        }
    }
}

onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
});

onUnmounted(() => {
    window.removeEventListener('scroll', onScroll);
});
</script>

<template>
    <Head title="Guide complet — Rewio" />

    <AppHeaderLayoutLanding :canRegister="canRegister">
        <!-- Hero -->
        <section class="border-b border-border/40 py-16">
            <div class="mx-auto max-w-5xl px-4 text-center">
                <span
                    class="mb-4 inline-block text-xs font-medium tracking-widest text-brand uppercase"
                >
                    Guide complet
                </span>
                <h1
                    class="text-3xl font-semibold tracking-tight text-foreground sm:text-5xl"
                >
                    De l'import à la rédaction,
                    <span class="text-brand">étape par étape</span>
                </h1>
                <p
                    class="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground"
                >
                    Ce guide illustré vous accompagne à travers les 13 étapes
                    d'une revue systématique complète avec Rewio — des premières
                    équations de recherche jusqu'à l'export de votre
                    bibliographie.
                </p>
                <div
                    class="mt-8 flex flex-wrap items-center justify-center gap-3"
                >
                    <Link
                        :href="register()"
                        class="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-brand/85"
                    >
                        Commencer gratuitement
                        <ChevronRight class="h-4 w-4" />
                    </Link>
                    <Link
                        :href="login()"
                        class="inline-flex items-center gap-2 rounded-full border border-border/60 px-5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                        Déjà inscrit ? Connexion
                    </Link>
                </div>
            </div>
        </section>

        <!-- Main content: sidebar + steps -->
        <div class="mx-auto max-w-6xl px-4 py-12">
            <div class="flex gap-10 lg:gap-16">
                <!-- Sticky sidebar -->
                <aside ref="sidebarRef" class="hidden w-56 shrink-0 lg:block">
                    <div class="sticky top-28">
                        <p
                            class="mb-3 text-[11px] font-semibold tracking-widest text-muted-foreground uppercase"
                        >
                            Étapes
                        </p>
                        <nav class="space-y-0.5">
                            <button
                                v-for="step in steps"
                                :key="step.number"
                                class="group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors"
                                :class="
                                    activeStep === step.number
                                        ? 'bg-brand/8 font-medium text-brand'
                                        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                                "
                                @click="scrollToStep(step.number)"
                            >
                                <span
                                    class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-colors"
                                    :class="
                                        activeStep === step.number
                                            ? 'bg-brand text-white'
                                            : 'bg-muted text-muted-foreground group-hover:bg-accent'
                                    "
                                >
                                    {{ step.number }}
                                </span>
                                <span class="truncate leading-snug">{{
                                    step.title
                                }}</span>
                            </button>
                        </nav>

                        <!-- CTA in sidebar -->
                        <div
                            class="mt-8 rounded-xl border border-brand/20 bg-brand/[0.04] p-4"
                        >
                            <p class="text-sm font-medium text-foreground">
                                Prêt à commencer ?
                            </p>
                            <p class="mt-1 text-xs text-muted-foreground">
                                Gratuit, sans carte bancaire.
                            </p>
                            <Link
                                :href="register()"
                                class="mt-3 flex w-full items-center justify-center rounded-full bg-brand px-3 py-2 text-xs font-medium text-white transition-all hover:bg-brand/85"
                            >
                                Créer mon compte
                            </Link>
                        </div>
                    </div>
                </aside>

                <!-- Steps content -->
                <div class="min-w-0 flex-1 space-y-20">
                    <section
                        v-for="step in steps"
                        :id="'step-' + step.number"
                        :key="step.number"
                        class="scroll-mt-28"
                    >
                        <!-- Step header -->
                        <div class="mb-6 flex items-start gap-4">
                            <div
                                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10"
                            >
                                <component
                                    :is="step.icon"
                                    class="h-5 w-5 text-brand"
                                />
                            </div>
                            <div>
                                <div class="flex items-center gap-2">
                                    <span
                                        class="text-[11px] font-semibold tracking-widest text-muted-foreground uppercase"
                                    >
                                        Étape {{ step.number }}
                                    </span>
                                </div>
                                <h2
                                    class="mt-0.5 text-xl font-semibold tracking-tight text-foreground sm:text-2xl"
                                >
                                    {{ step.title }}
                                </h2>
                                <p class="mt-1 text-sm text-muted-foreground">
                                    {{ step.subtitle }}
                                </p>
                            </div>
                        </div>

                        <!-- Description -->
                        <p
                            class="text-base leading-relaxed text-muted-foreground"
                        >
                            {{ step.description }}
                        </p>

                        <!-- Screenshot -->
                        <div
                            class="my-6 overflow-hidden rounded-xl border border-border/60 shadow-sm"
                        >
                            <img
                                :src="step.screenshot"
                                :alt="step.screenshotAlt"
                                class="w-full object-cover"
                                loading="lazy"
                            />
                        </div>

                        <!-- Tips -->
                        <div
                            class="rounded-xl border border-amber-200/60 bg-amber-50/60 p-4 dark:border-amber-800/30 dark:bg-amber-900/10"
                        >
                            <div class="mb-2 flex items-center gap-2">
                                <Lightbulb
                                    class="h-4 w-4 text-amber-600 dark:text-amber-400"
                                />
                                <span
                                    class="text-xs font-semibold tracking-wider text-amber-700 uppercase dark:text-amber-400"
                                >
                                    Conseils
                                </span>
                            </div>
                            <ul class="space-y-1.5">
                                <li
                                    v-for="tip in step.tips"
                                    :key="tip"
                                    class="flex items-start gap-2 text-sm text-amber-900/80 dark:text-amber-200/80"
                                >
                                    <CheckCircle2
                                        class="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
                                    />
                                    {{ tip }}
                                </li>
                            </ul>
                        </div>
                    </section>

                    <!-- Final CTA -->
                    <section
                        class="rounded-2xl border border-brand/20 bg-brand/[0.03] p-8 text-center"
                    >
                        <h2
                            class="text-2xl font-semibold tracking-tight text-foreground"
                        >
                            Votre revue commence maintenant
                        </h2>
                        <p class="mx-auto mt-3 max-w-md text-muted-foreground">
                            Rewio est gratuit pour commencer. L'IA est
                            disponible en option. Aucune carte bancaire requise.
                        </p>
                        <div
                            class="mt-6 flex flex-wrap items-center justify-center gap-3"
                        >
                            <Link
                                :href="register()"
                                class="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white transition-all hover:bg-brand/85"
                            >
                                Créer mon compte gratuitement
                                <ChevronRight class="h-4 w-4" />
                            </Link>
                            <Link
                                :href="login()"
                                class="inline-flex items-center rounded-full border border-border/60 px-6 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                            >
                                J'ai déjà un compte
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    </AppHeaderLayoutLanding>
</template>
